import Vue from 'vue'
import VueRouter from 'vue-router'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import locale from './../lang/i18n'
import Cookies from 'js-cookie'
import { applyPageSeo, defaultPageSeo, fetchPublicSeo, localeFromPath, logicalPath } from '@/utils/seo'

Vue.use(VueRouter)

function defaultLocaleRoute (route) {
  const preferredLocale = Cookies.get('zhy_locale')
  const browserLocale = typeof navigator !== 'undefined'
    ? navigator.language || navigator.userLanguage
    : ''
  const value = String(preferredLocale || browserLocale || '').toLowerCase()
  return {
    path: value.startsWith('zh') ? '/zh-cn/' : '/en/',
    query: route.query,
    hash: route.hash
  }
}

const routes = [
  { path: '/', redirect: defaultLocaleRoute },
  {
    path: '/:locale(en|zh-cn)',
    component: () => import('@/views/home/home.vue'),
    children: [
      { path: '', name: 'public-home', component: () => import('@/views/home/index.vue') },
      { path: 'about', name: 'about', component: () => import('@/views/home/aboutUs.vue') },
      {
        path: 'news',
        component: () => import('@/views/home/aboutNews.vue'),
        children: [
          { path: '', name: 'news', component: () => import('@/views/home/news.vue') },
          { path: ':slug', name: 'news-detail', component: () => import('@/views/home/newsContent.vue') }
        ]
      },
      {
        path: 'products',
        component: () => import('@/views/home/aboutProducts.vue'),
        children: [
          { path: '', name: 'products', component: () => import('@/views/home/productSeries.vue') },
          { path: ':slug', name: 'product-detail', component: () => import('@/views/home/productList.vue') }
        ]
      },
      { path: 'videos', name: 'videos', component: () => import('@/views/home/videos.vue') },
      { path: 'videos/:slug', name: 'video-detail', component: () => import('@/views/home/videoContent.vue') },
      {
        path: 'case-studies',
        component: () => import('@/views/home/aboutCase.vue'),
        children: [
          { path: '', name: 'case-studies', component: () => import('@/views/home/cases.vue') },
          { path: ':slug', name: 'case-study-detail', component: () => import('@/views/home/caseContent.vue') }
        ]
      },
      { path: 'contact', name: 'contact', component: () => import('@/views/home/aboutContact.vue') },
      { path: 'resources', name: 'resources', component: () => import('@/views/home/aboutTechnical.vue') }
    ]
  },
  {
    path: '/management/login',
    name: 'admin-login',
    component: () => import('@/views/management/login.vue')
  },
  {
    path: '/management',
    component: () => import('@/views/management/management.vue'),
    redirect: '/management/home',
    children: [
      { path: 'home', name: 'admin-home', component: () => import('@/views/management/home.vue') },
      { path: 'editSystemSettings', name: 'admin-settings', component: () => import('@/views/management/editSystemSettings.vue') },
      { path: 'editAboutWe', name: 'admin-about', component: () => import('@/views/management/editAboutWe.vue') },
      { path: 'editNewsList', name: 'admin-news', component: () => import('@/views/management/editNewsList.vue') },
      { path: 'editNewsItem/:id?', name: 'editNewsItem', component: () => import('@/views/management/editNewsItem.vue') },
      { path: 'editProductList', name: 'admin-products', component: () => import('@/views/management/editProductList.vue') },
      { path: 'editProductItem/:id?', name: 'editProductItem', component: () => import('@/views/management/editProductItem.vue') },
      { path: 'editCustomerCase', name: 'admin-cases', component: () => import('@/views/management/editCustomerCase.vue') },
      { path: 'editCustomerCaseItem/:id?', name: 'editCustomerCaseItem', component: () => import('@/views/management/editCustomerCaseItem.vue') },
      { path: 'editContactUs', name: 'admin-contact', component: () => import('@/views/management/editContactUs.vue') },
      { path: 'editTechnical', name: 'admin-resources', component: () => import('@/views/management/editTechnical.vue') },
      { path: 'editTechnicalItem/:id?', name: 'editTechnicalItem', component: () => import('@/views/management/editTechnicalItem.vue') }
    ]
  }
]

const router = new VueRouter({ mode: 'history', routes })

NProgress.configure({ showSpinner: false })

function canonicalSeoPath (path) {
  const value = logicalPath(path)
  return value === '/' ? '/' : `${value.replace(/\/$/, '')}/`
}

function seoForRoute (path, currentLocale) {
  const english = currentLocale === 'en-US'
  const value = canonicalSeoPath(path)
  if (value === '/') return defaultPageSeo(currentLocale)
  const labels = english
    ? { about: 'About Us', news: 'News', products: 'Products', cases: 'Case Studies', resources: 'Resources', contact: 'Contact', videos: 'Videos' }
    : { about: '关于我们', news: '新闻资讯', products: '产品中心', cases: '项目案例', resources: '技术文档', contact: '联系我们', videos: '视频中心' }
  let section = labels.about
  if (value.startsWith('/news/')) section = labels.news
  else if (value.startsWith('/products/')) section = labels.products
  else if (value.startsWith('/case-studies/')) section = labels.cases
  else if (value.startsWith('/resources/')) section = labels.resources
  else if (value.startsWith('/contact/')) section = labels.contact
  else if (value.startsWith('/videos/')) section = labels.videos
  return {
    locale: currentLocale,
    path: value,
    title: `${section} | ${english ? 'GZZHYUN' : '广州智会云'}`,
    description: english
      ? `${section} from Guangzhou Zhihui Cloud Technology Co., Ltd.`
      : `广州智会云科技有限公司${section}。`
  }
}

function updateDetailSeo (path, currentLocale) {
  const value = canonicalSeoPath(path)
  const detail = value.match(/^\/(news|case-studies|products|videos)\/([^/]+)\/?$/)
  if (!detail) return
  const kind = detail[1]
  const slug = detail[2]
  const request = kind === 'case-studies'
    ? fetchPublicSeo(currentLocale, 'case-studies', slug)
    : kind === 'products'
      ? fetchPublicSeo(currentLocale, 'product-series', slug).catch(() => fetchPublicSeo(currentLocale, 'products', slug))
      : kind === 'videos'
        ? fetchPublicSeo(currentLocale, 'products', slug)
        : fetchPublicSeo(currentLocale, 'news', slug)
  request.then(data => {
    const title = data.seoTitle || data.title || data.name
    if (!title) return
    const type = kind === 'news' || kind === 'case-studies'
      ? 'Article'
      : kind === 'products'
        ? 'CollectionPage'
        : 'VideoObject'
    applyPageSeo({
      locale: currentLocale,
      path: value,
      title: `${title} | ${currentLocale === 'en-US' ? 'GZZHYUN' : '广州智会云'}`,
      description: data.description || data.metaDescription || title,
      image: data.coverUrl,
      type,
      headline: data.title || data.name || title,
      videoUrl: data.videoUrl,
      uploadDate: data.publishDate
    })
  }).catch(() => {})
}

router.beforeEach((to, from, next) => {
  NProgress.start()
  if (/^\/(?:en|zh-cn)(?:\/.*)?$/i.test(to.path) && !to.path.endsWith('/')) {
    next({ path: `${to.path}/`, query: to.query, hash: to.hash, replace: true })
    return
  }
  const token = Cookies.get('TokenKey')
  const isLogin = to.path === '/management/login'
  const isAdmin = to.path.startsWith('/management') && !isLogin

  if (isLogin && token) {
    next({ path: '/management/home' })
    return
  }
  if (isAdmin && !token) {
    next({ path: '/management/login' })
    return
  }

  const pathLocale = localeFromPath(to.path)
  if (pathLocale) {
    if (locale.locale !== pathLocale) locale.locale = pathLocale
    localStorage.setItem('locale', pathLocale)
    applyPageSeo(seoForRoute(to.path, pathLocale))
    updateDetailSeo(to.path, pathLocale)
  }
  next()
})

router.afterEach(() => NProgress.done())

export default router
